
import React from 'react';
import FeatureCard from './FeatureCard';
import type { Feature, Page, CurrentUser } from '../types';

// Importing icons
import PointOfSaleIcon from './icons/PointOfSaleIcon';
import ProductsIcon from './icons/ProductsIcon';
import PurchasingIcon from './icons/PurchasingIcon';
import InventoryIcon from './icons/InventoryIcon';
import CustomersIcon from './icons/CustomersIcon';
import ReportsIcon from './icons/ReportsIcon';
import ExpensesIcon from './icons/ExpensesIcon';
import QrCodeIcon from './icons/QrCodeIcon';
import UserRolesIcon from './icons/UserRolesIcon';
import LoyaltyIcon from './icons/LoyaltyIcon';
import SmartpayIcon from './icons/SmartpayIcon';
import DeliveryIcon from './icons/DeliveryIcon';
import StaffIcon from './icons/StaffIcon';
import TableIcon from './icons/TableIcon';
import CalendarIcon from './icons/CalendarIcon';

const features: Feature[] = [
  {
    icon: PointOfSaleIcon,
    title: 'Point of Sale',
    description: 'Efficient and fast checkout process tailored for restaurants.',
    pageLink: 'pos',
    requiredPermission: 'access_pos',
  },
  {
    icon: ProductsIcon,
    title: 'Menu Management',
    description: 'Manage an unlimited number of menu items, categories, and modifiers.',
    pageLink: 'menu',
    requiredPermission: 'manage_menu',
  },
   {
    icon: TableIcon,
    title: 'Table Management',
    description: 'Manage your floor plan, table status, and merge tables for large groups.',
    pageLink: 'table-management',
    requiredPermission: 'manage_tables',
  },
  {
    icon: CalendarIcon,
    title: 'Reservations',
    description: 'Manage table reservations, view upcoming bookings, and track customer reservations.',
    pageLink: 'reservations',
    requiredPermission: 'manage_tables',
  },
  {
    icon: InventoryIcon,
    title: 'Inventory Management',
    description: 'Track stock levels in real-time and get alerts for low inventory.',
    pageLink: 'inventory',
    requiredPermission: 'manage_inventory',
  },
  {
    icon: CustomersIcon,
    title: 'Customer Management',
    description: 'Manage customer profiles, order history, and contact records.',
    pageLink: 'customers',
    requiredPermission: 'manage_customers',
  },
  {
    icon: LoyaltyIcon,
    title: 'Customer Loyalty',
    description: 'Implement points-based loyalty programs to retain customers.',
    pageLink: 'loyalty',
    requiredPermission: 'manage_loyalty',
  },
  {
    icon: QrCodeIcon,
    title: 'Table QR Ordering',
    description: 'Allow customers to view the menu and place orders from their table.',
    pageLink: 'qr-management',
    requiredPermission: 'manage_tables',
  },
  {
    icon: StaffIcon,
    title: 'Staff Management',
    description: 'Manage staff roles, permissions, and track performance.',
    pageLink: 'staff',
    requiredPermission: 'manage_staff',
  },
  {
    icon: UserRolesIcon,
    title: 'User Roles',
    description: 'Define roles and manage permissions for your staff members.',
    pageLink: 'user-roles',
    requiredPermission: 'manage_settings',
  },
  {
    icon: ReportsIcon,
    title: 'Detailed Reports',
    description: 'Gain insights with comprehensive reporting on sales and performance.',
    pageLink: 'reports',
    requiredPermission: 'access_reports',
  },
  {
    icon: ExpensesIcon,
    title: 'Expenses',
    description: 'Record daily expenses, attach receipts, and export for GST and tax reconciliation.',
    pageLink: 'expenses',
  },
  {
    icon: PurchasingIcon,
    title: 'Purchasing & Suppliers',
    description: 'Manage suppliers and purchase orders; update inventory when goods arrive.',
    pageLink: 'purchasing',
    requiredPermission: 'manage_purchasing',
  },
  {
    icon: ExpensesIcon,
    title: 'Invoices',
    description: 'Record supplier tax invoices and issue client tax invoices (e.g. breakfast billing).',
    pageLink: 'invoices',
    requiredPermission: 'manage_invoices',
  },
  {
    icon: DeliveryIcon,
    title: 'Delivery Integrations',
    description: 'Receive and manage orders directly from Uber Eats and DoorDash.',
    pageLink: 'delivery',
    requiredPermission: 'manage_delivery',
  },
  {
    icon: SmartpayIcon,
    title: 'Smartpay Integration',
    description: 'Seamlessly integrate with Smartpay for payment processing in NZ.',
    pageLink: 'settings',
    requiredPermission: 'manage_settings',
  },
  {
    icon: ReportsIcon,
    title: 'Coupon Campaigns',
    description: 'Create campaign batches, export unique coupon codes, and print A4 coupon sheets.',
    pageLink: 'coupon-campaigns',
    requiredPermission: 'manage_settings',
  },
];

interface FeaturesSectionProps {
  onFeatureClick: (page: Page) => void;
  currentUser: CurrentUser;
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({ onFeatureClick, currentUser }) => {
  // Debug logging
  React.useEffect(() => {
    console.log('📊 FeaturesSection - Current User:', {
      name: currentUser.name,
      role: currentUser.role,
      isAdmin: currentUser.isAdmin,
      permissionsCount: currentUser.permissions.size,
      permissions: Array.from(currentUser.permissions),
      permissionsType: currentUser.permissions.constructor.name
    });
  }, [currentUser]);

  return (
    <section id="features" className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            System Modules
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Everything you need to run your restaurant smoothly.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {features.map((feature, index) => {
            const hasPermission = feature.requiredPermission ? currentUser.permissions.has(feature.requiredPermission) : true;
            if (feature.requiredPermission && !hasPermission) {
              console.log(`🚫 Feature "${feature.title}" disabled - missing permission: ${feature.requiredPermission}`);
            }
            return (
              <FeatureCard 
                key={index} 
                feature={feature} 
                onFeatureClick={onFeatureClick} 
                disabled={!hasPermission}
              />
            )
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
